import { cabinetHandleRefresh } from '@/fsd/entities/cabinet/lib/helpers/cabinetHandlerRefresh';
import { ICabinetModalType } from '@/fsd/entities/cabinet/model/types/ICabinetModalType';
import { CabinetModal } from '@/fsd/entities/cabinet/ui/CabinetModal/CabinetModal';
import { EditIcon } from '@/fsd/shared/ui/icons/edit';
import { Button } from '@fsd/shared/ui';
import { ChevronDown, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { CabinetApi } from '../../../entities/cabinet/api';
import { ICurrentCabinetData, OwnCabinet } from '../models';
import styles from './toggle-cabinet.module.scss';

const titleText = {
  create: 'Новый кабинет',
  remove: 'Удалить кабинет',
  rename: 'Переименовать кабинет',
};

const contentText = {
  create: '',
  remove: '',
  rename: 'Переименовать кабинет',
};

export const ToggleCabinet = () => {
  const [loading, setLoading] = useState(true);
  const [currentCabinet, setCurrentCabinet] =
    useState<ICurrentCabinetData | null>(null);
  const [cabinets, setCabinets] = useState<OwnCabinet[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [newCabinetName, setNewCabinetName] = useState<string>('');
  const [error, setError] = useState(false);

  const typeModal = useRef<ICabinetModalType>('create');
  const CabinetIdRef = useRef<number | null>(null);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const fetchCabinets = async () => {
    const { load, current } = CabinetApi;

    try {
      const [cabinetsData, currentData] = await Promise.all([
        load(),
        current(),
      ]);

      const newCabinets: OwnCabinet[] = [
        ...(cabinetsData?.data.member_cabinets?.map(
          (cabinet) => cabinet.cabinet
        ) || []),
      ];

      setCurrentCabinet(currentData?.data || null);
      setCabinets(newCabinets);
      setError(false);
    } catch (error) {
      console.error('Error fetching cabinets:', error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCabinets();
  }, []);

  const handlerToggleCabinet = async (cabinet_id: number) => {
    await CabinetApi.switch(cabinet_id);
    await fetchCabinets();
    cabinetHandleRefresh();
  };

  const openRenameModel = async (current_id: number) => {
    setNewCabinetName('');
    typeModal.current = 'rename';
    const renameCabinetRecord = cabinets.find(({ id }) => id === current_id);
    CabinetIdRef.current = renameCabinetRecord!.id;
    setNewCabinetName(renameCabinetRecord!.name);
    handleOpenModal();
  };

  const openRemoveModel = async (id: number) => {
    CabinetIdRef.current = id;
    typeModal.current = 'remove';
    handleOpenModal();
  };

  const openCreateModel = async () => {
    setNewCabinetName('');
    typeModal.current = 'create';
    handleOpenModal();
  };

  const handleCreateCabinet = async () => {
    await CabinetApi.create(newCabinetName);
    handleCloseModal();
    await fetchCabinets();
    cabinetHandleRefresh();
  };

  const handleChangeName = async () => {
    await CabinetApi.rename(CabinetIdRef.current!, newCabinetName);
    handleCloseModal();
    await fetchCabinets();
  };

  const handleRemoveCabinet = async () => {
    await CabinetApi.remove(CabinetIdRef.current!);
    CabinetIdRef.current = null;
    await fetchCabinets();
    handleCloseModal();
  };

  if (error) {
    return;
  }

  if (loading) {
    return;
  }

  return (
    <>
      <div className={styles.container}>
        <h6 className={styles.title}>
          <span
            style={{
              maxWidth: '210px',
              textAlign: 'right',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              padding: '5px',
              textOverflow: 'ellipsis',
            }}
          >
            {currentCabinet?.user_id &&
              cabinets.find(({ id }) => id === currentCabinet?.cabinet_id)
                ?.name}
          </span>
          <ChevronDown />
        </h6>

        <ul className={styles.cabinets}>
          <li>
            <Button onClick={openCreateModel} variant="link">
              Добавить кабинет
            </Button>
          </li>
          <div className={styles.divider} />
          {cabinets.map(({ id, name }) => (
            <li key={id} className={styles.cabinets__item}>
              <Button
                onClick={() => handlerToggleCabinet(id)}
                variant="link"
                style={{
                  background: 'transparent',
                  overflow: 'hidden',
                  textAlign: 'left',
                }}
              >
                <p
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '100%',
                  }}
                >
                  {name}
                </p>
              </Button>
              <div style={{ display: 'flex', gap: '5px' }}>
                <span onClick={() => openRenameModel(id)}>
                  <EditIcon
                    className={styles.edit_icon}
                    stroke="currentColor"
                  />
                </span>
                <span
                  onClick={() => openRemoveModel(id)}
                  className={styles.cabinets__item_trash}
                >
                  <Trash2 strokeWidth="2" width={16} height={16} />
                </span>
              </div>
            </li>
          ))}
        </ul>
        <CabinetModal
          typeModal={typeModal}
          setNewCabinetName={setNewCabinetName}
          openModal={openModal}
          newCabinetName={newCabinetName}
          handleCloseModal={handleCloseModal}
          titleText={titleText[typeModal.current]}
          contentText={contentText[typeModal.current]}
          handleChangeName={handleChangeName}
          handleCreateCabinet={handleCreateCabinet}
          handleRemoveCabinet={handleRemoveCabinet}
        />
      </div>
    </>
  );
};
